import { FALLBACK_PART1 } from "./content.js";
import { buildAssessmentWav } from "./audio-recorder.js";
import { getPreferences, getPrompts, getState, listCards, listSessions, saveSession, saveState } from "./storage.js";

/**
 * 将 IELTS 四项平均分按 0.5 分档取整。
 *
 * @param {Record<string, number>} scores 四项分数。
 * @returns {number} 0 到 9 的半分档总分。
 */
export function calculateOverallBand(scores) {
  const keys = ["fluency_coherence", "lexical_resource", "grammatical_range_accuracy", "pronunciation"];
  const values = keys.map((key) => Math.min(9, Math.max(0, Number(scores?.[key] || 0))));
  return Math.floor((values.reduce((total, value) => total + value, 0) / values.length) * 2 + 0.5) / 2;
}

/**
 * 在给定毫秒数内等待异步结果，超时后抛出固定错误。
 *
 * @param {Promise<any>} promise 原始异步操作。
 * @param {number} timeoutMs 超时毫秒数。
 * @returns {Promise<any>} 原始结果。
 */
export function withTimeout(promise, timeoutMs) {
  if (timeoutMs <= 0) return Promise.reject(new Error("动态追问已关闭"));
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("动态追问超时")), timeoutMs);
    promise.then((value) => { window.clearTimeout(timer); resolve(value); }, (error) => { window.clearTimeout(timer); reject(error); });
  });
}

/**
 * 管理全部手机端口语练习流程，服务端只处理无状态模型调用。
 */
export class PracticeEngine extends EventTarget {
  /**
   * 创建客户端练习引擎。
   *
   * @param {{gateway: import('./gateway.js').GatewayClient}} dependencies 网关依赖。
   */
  constructor({ gateway }) {
    super();
    this.gateway = gateway;
    this.reset();
  }

  /**
   * 清理当前练习的内存状态。
   *
   * @returns {void}
   */
  reset() {
    this.sessionId = crypto.randomUUID();
    this.mode = "daily";
    this.style = "standard";
    this.part = "";
    this.cardSource = "bank";
    this.card = null;
    this.questions = [];
    this.questionIndex = 0;
    this.currentQuestion = "";
    this.currentQuestionKind = "main";
    this.currentQuestionSource = "preset";
    this.currentQuestionAudio = null;
    this.helpUsedForCurrent = false;
    this.answers = [];
    this.dynamicCounts = { part1: 0, part3: 0 };
    this.preferences = null;
    this.prompts = null;
    this.startedAt = 0;
    this.partStartedAt = 0;
    this.partTimings = {};
    this.active = false;
    this.usedCardIds = new Set();
  }

  /**
   * 向界面发送一个稳定的练习事件。
   *
   * @param {string} type 事件名称。
   * @param {object} detail 事件数据。
   * @returns {void}
   */
  emit(type, detail = {}) {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  /**
   * 重新读取本地偏好和提示词。
   *
   * @returns {Promise<void>} 配置加载完成结果。
   */
  async refreshConfiguration() {
    [this.preferences, this.prompts] = await Promise.all([getPreferences(), getPrompts()]);
  }

  /**
   * 开始一种练习模式。
   *
   * @param {"daily"|"part1"|"part2"|"part3"|"mock"} mode 练习模式。
   * @param {{style?: string, cardSource?: string}} options 日常风格和题卡来源。
   * @returns {Promise<void>} 首题准备完成结果。
   */
  async start(mode, { style = "standard", cardSource = "bank" } = {}) {
    this.reset();
    await this.refreshConfiguration();
    this.mode = mode;
    this.style = style === "american" ? "american" : "standard";
    this.cardSource = cardSource === "generated" ? "generated" : "bank";
    this.active = true;
    this.startedAt = Date.now();
    this.emit("state", { state: "preparing" });
    if (mode === "daily") {
      this.emit("daily-ready", { style: this.style });
      return;
    }
    if (mode === "part1" || mode === "mock") await this.startPart1();
    else if (mode === "part2") await this.startPart2();
    else if (mode === "part3") await this.startPart3();
    else throw new Error("不支持的练习模式");
    await this.persistActiveState();
  }

  /**
   * 结束当前练习并删除可恢复状态。
   *
   * @returns {Promise<void>} 停止完成结果。
   */
  async stop() {
    this.active = false;
    await saveState("activePractice", null);
    this.emit("stopped");
  }

  /**
   * 分析一轮日常对话并保存本机历史。
   *
   * @param {{pcm: Int16Array, wav: Blob, durationSeconds: number}} recording 完整用户录音。
   * @returns {Promise<object>} 日常对话结构化结果。
   */
  async submitDaily(recording) {
    await this.refreshConfiguration();
    this.emit("state", { state: "analyzing" });
    const recentSessions = (await listSessions(Number.MAX_SAFE_INTEGER))
      .filter((session) => session.kind === "daily")
      .slice(0, 5)
      .reverse()
      .map((session) => ({ user: session.result?.recognized_text || "", assistant: session.result?.assistant_reply || "" }));
    const result = await this.gateway.runAI("daily", {
      provider: this.preferences.audioProvider,
      style: this.style,
      recentContext: recentSessions,
      prompts: this.prompts,
    }, recording.wav);
    if (result.status === "too_short") throw new Error("录音内容太短，请说出一个完整句子");
    if (result.status === "no_speech") throw new Error("没有识别到可用语音，请重新录制");
    if (result.status === "non_english") throw new Error("请使用英语完成本轮对话");
    let assistantAudio = null;
    try {
      assistantAudio = await this.gateway.synthesize(result.assistant_reply, this.preferences.speechProvider, this.style);
    } catch (error) {
      this.emit("speech-degraded", { message: error.message });
    }
    const sessionId = crypto.randomUUID();
    const audioEntries = [];
    if (this.preferences.saveRecordings) {
      audioEntries.push({ id: `${sessionId}-user`, kind: "user", mimeType: recording.wav.type, blob: recording.wav });
      if (assistantAudio) audioEntries.push({ id: `${sessionId}-assistant`, kind: "assistant", mimeType: assistantAudio.type, blob: assistantAudio });
    }
    const session = await saveSession({
      id: sessionId,
      kind: "daily",
      title: this.style === "american" ? "美式日常对话" : "日常对话",
      style: this.style,
      durationSeconds: recording.durationSeconds,
      result,
    }, audioEntries);
    this.emit("daily-result", { result, session, assistantAudio });
    this.emit("state", { state: "ready" });
    return result;
  }

  /**
   * 准备 PART1 的八道问题，模型不可用时使用本地固定题目。
   *
   * @returns {Promise<void>} 第一题准备完成结果。
   */
  async startPart1() {
    this.part = "part1";
    this.partStartedAt = Date.now();
    let questionSet = FALLBACK_PART1;
    try {
      questionSet = await this.gateway.runAI("part1", { provider: this.preferences.textProvider, prompts: this.prompts });
    } catch (error) {
      this.emit("fallback", { message: "问题生成不可用，已使用本地 PART1 题目", error });
    }
    this.questions = Array.isArray(questionSet.questions) && questionSet.questions.length === 8 ? questionSet.questions : FALLBACK_PART1.questions;
    this.questionIndex = 0;
    this.emit("part-started", { part: "part1", total: this.questions.length, topics: questionSet.topics || FALLBACK_PART1.topics });
    await this.presentQuestion(this.questions[0], "main", "model_generated");
  }

  /**
   * 选择或生成 PART2 题卡并开始一分钟准备。
   *
   * @returns {Promise<void>} 题卡准备完成结果。
   */
  async startPart2() {
    this.part = "part2";
    this.partStartedAt = Date.now();
    this.card = await this.selectCueCard();
    this.currentQuestion = this.card.title;
    this.currentQuestionKind = "part2_long";
    this.currentQuestionSource = "cue_card";
    this.currentQuestionAudio = null;
    this.emit("part2-card", { card: this.card, preparationSeconds: 60 });
  }

  /**
   * 抽取启用题卡，或请求模型生成后在失败时回退本地题库。
   *
   * @returns {Promise<object>} 当前 PART2 题卡。
   */
  async selectCueCard() {
    const enabledCards = await listCards({ enabledOnly: true });
    if (this.cardSource === "generated") {
      try {
        return await this.gateway.runAI("cue-card", { provider: this.preferences.textProvider, prompts: this.prompts });
      } catch (error) {
        this.emit("fallback", { message: "随机题卡生成失败，已改用本地题库", error });
      }
    }
    if (!enabledCards.length) throw new Error("题库中没有启用的题卡");
    const unused = enabledCards.filter((card) => !this.usedCardIds.has(card.id));
    const candidates = unused.length ? unused : enabledCards;
    if (!unused.length) this.usedCardIds.clear();
    const selected = candidates[Math.floor(Math.random() * candidates.length)];
    this.usedCardIds.add(selected.id);
    return selected;
  }

  /**
   * 结束 PART2 准备阶段并显示陈述题。
   *
   * @returns {Promise<void>} 题目展示完成结果。
   */
  async finishPart2Preparation() {
    if (this.part !== "part2" || !this.card) throw new Error("当前不在 PART2 准备阶段");
    await this.presentQuestion(this.card.title, "part2_long", "cue_card", false);
  }

  /**
   * 使用最近完成的 PART2 上下文开始 PART3。
   *
   * @returns {Promise<void>} 第一题准备完成结果。
   */
  async startPart3() {
    const context = this.mode === "mock" && this.card
      ? { card: this.card, answers: this.answers.filter((answer) => answer.part === "part2") }
      : await getState("part2Context");
    if (!context?.card) throw new Error("请先在这台 iPhone 上完成一次 PART2");
    this.card = context.card;
    if (this.mode === "part3") this.answers = Array.isArray(context.answers) ? context.answers : [];
    const transcript = (context.answers || []).map((answer) => answer.transcript).join("\n");
    let questions = this.card.part3_questions.slice(0, 4);
    try {
      const result = await this.gateway.runAI("part3", {
        provider: this.preferences.textProvider,
        prompts: this.prompts,
        card: this.card,
        part2Transcript: transcript,
      });
      if (Array.isArray(result.questions) && result.questions.length === 4) questions = result.questions;
    } catch (error) {
      this.emit("fallback", { message: "讨论题生成不可用，已使用题卡候选问题", error });
    }
    this.part = "part3";
    this.partStartedAt = Date.now();
    this.questions = questions;
    this.questionIndex = 0;
    this.emit("part-started", { part: "part3", total: questions.length });
    await this.presentQuestion(questions[0], "main", "model_generated");
  }

  /**
   * 合成并展示一道考官问题；语音失败时仍保留文本流程。
   *
   * @param {string} question 问题文本。
   * @param {string} kind main、part2_long 或 part2_rounding。
   * @param {string} source 问题来源。
   * @param {boolean} synthesize 是否请求考官语音。
   * @returns {Promise<void>} 问题准备完成结果。
   */
  async presentQuestion(question, kind = "main", source = "preset", synthesize = true) {
    this.currentQuestion = question;
    this.currentQuestionKind = kind;
    this.currentQuestionSource = source;
    this.currentQuestionAudio = null;
    this.helpUsedForCurrent = false;
    if (synthesize) {
      try {
        this.currentQuestionAudio = await this.gateway.synthesize(question, this.preferences.speechProvider);
      } catch (error) {
        this.emit("speech-degraded", { message: "考官语音不可用，已切换为文字题目", error });
      }
    }
    this.emit("question", {
      part: this.part,
      index: kind.startsWith("part2") ? (kind === "part2_long" ? 1 : 2) : this.questionIndex + 1,
      total: this.part === "part2" ? 2 : this.questions.length,
      text: question,
      kind,
      source,
      audio: this.currentQuestionAudio,
    });
    await this.persistActiveState();
  }

  /**
   * 为当前 IELTS 问题请求关键词、句型和短例句。
   *
   * @returns {Promise<object>} 答题帮助内容。
   */
  async requestHelp() {
    if (!this.currentQuestion) throw new Error("当前没有可提供帮助的问题");
    this.helpUsedForCurrent = true;
    const content = await this.gateway.runAI("help", {
      provider: this.preferences.textProvider,
      prompts: this.prompts,
      section: this.part,
      question: this.currentQuestion,
    });
    this.emit("help", { question: this.currentQuestion, content });
    return content;
  }

  /**
   * 转写一轮 IELTS 回答并推进问题或生成最终评分。
   *
   * @param {{pcm: Int16Array, wav: Blob, durationSeconds: number}} recording 用户回答。
   * @returns {Promise<void>} 当前轮处理完成结果。
   */
  async submitExamAnswer(recording) {
    if (!this.currentQuestion) throw new Error("当前没有待回答的问题");
    this.emit("state", { state: "transcribing" });
    const transcriptResult = await this.gateway.runAI("transcribe", {
      provider: this.preferences.transcriptionProvider,
    }, recording.wav);
    const transcript = String(transcriptResult.text || "").trim();
    if (!transcript) throw new Error("没有识别到清晰的回答，请重新录制");
    const answer = {
      id: crypto.randomUUID(),
      part: this.part,
      question: this.currentQuestion,
      transcript,
      durationSeconds: recording.durationSeconds,
      questionSource: this.currentQuestionSource,
      usedHelp: this.helpUsedForCurrent,
      pcm: recording.pcm,
      userAudio: recording.wav,
      questionAudio: this.currentQuestionAudio,
    };
    this.answers.push(answer);
    this.emit("transcript", { answer: this.publicAnswer(answer) });
    if (this.currentQuestionKind === "part2_long") {
      await this.presentQuestion(this.card.rounding_question, "part2_rounding", "preset");
      return;
    }
    if (this.currentQuestionKind === "part2_rounding") {
      await this.completePart2();
      return;
    }
    await this.advanceQuestion(answer);
  }

  /**
   * 推进 PART1 或 PART3，并在配置时限内尝试一次动态追问。
   *
   * @param {object} answer 刚完成的回答。
   * @returns {Promise<void>} 下一题或评分准备完成结果。
   */
  async advanceQuestion(answer) {
    this.questionIndex += 1;
    if (this.questionIndex >= this.questions.length) {
      await this.finishCurrentPart();
      return;
    }
    const limit = this.part === "part1" ? 1 : 2;
    const timeoutSeconds = Number(this.preferences.dynamicFollowupSeconds || 0);
    const canFollow = transcriptHasContent(answer.transcript) && this.dynamicCounts[this.part] < limit && timeoutSeconds > 0;
    if (canFollow) {
      try {
        const result = await withTimeout(this.gateway.runAI("followup", {
          provider: this.preferences.textProvider,
          section: this.part,
          question: answer.question,
          answer: answer.transcript,
        }), timeoutSeconds * 1_000);
        if (result.question) {
          this.dynamicCounts[this.part] += 1;
          this.questions[this.questionIndex] = result.question;
          await this.presentQuestion(result.question, "main", "dynamic_followup");
          return;
        }
      } catch {
        this.emit("fallback", { message: "动态追问未及时完成，已使用下一道主问题" });
      }
    }
    await this.presentQuestion(this.questions[this.questionIndex], "main", "preset");
  }

  /**
   * 保存 PART2 上下文并进入 PART3 或单项评分。
   *
   * @returns {Promise<void>} 后续阶段完成结果。
   */
  async completePart2() {
    this.partTimings.part2 = (Date.now() - this.partStartedAt) / 1_000;
    const part2Answers = this.answers.filter((answer) => answer.part === "part2").map((answer) => this.publicAnswer(answer));
    await saveState("part2Context", { card: this.card, answers: part2Answers, completedAt: new Date().toISOString() });
    if (this.mode === "mock") await this.startPart3();
    else await this.finishAssessment("part2", false);
  }

  /**
   * 完成 PART1 或 PART3，并按模式切换下一部分或评分。
   *
   * @returns {Promise<void>} 阶段切换完成结果。
   */
  async finishCurrentPart() {
    this.partTimings[this.part] = (Date.now() - this.partStartedAt) / 1_000;
    if (this.mode === "mock" && this.part === "part1") {
      await this.startPart2();
      return;
    }
    if (this.mode === "mock" && this.part === "part3") {
      await this.finishAssessment("mock", true);
      return;
    }
    await this.finishAssessment(this.part, false);
  }

  /**
   * 汇总转写和抽样音频，获取 IELTS 四项评分并保存历史。
   *
   * @param {string} section 评分范围。
   * @param {boolean} fullMock 是否为完整模考。
   * @returns {Promise<object>} 评分结果。
   */
  async finishAssessment(section, fullMock) {
    this.emit("state", { state: "scoring" });
    const records = fullMock ? this.answers : this.answers.filter((answer) => answer.part === section);
    const transcript = records.map((answer, index) => `Turn ${index + 1}\nExaminer: ${answer.question}\nCandidate: ${answer.transcript}`).join("\n\n");
    const assessmentAudio = buildAssessmentWav(records.map((answer) => answer.pcm));
    const result = await this.gateway.runAI("assess", {
      provider: this.preferences.audioProvider,
      prompts: this.prompts,
      section,
      fullMock,
      transcript,
    }, assessmentAudio);
    if (result.status === "insufficient_speech") throw new Error("回答内容不足，暂时无法生成可靠评分");
    if (result.status === "no_speech") throw new Error("没有识别到可用于评分的语音");
    result.overall_band = calculateOverallBand(result.scores || {});
    result.max_band = 9;
    result.disclaimer = "AI 练习估分，非 IELTS 官方成绩";
    const sessionId = this.sessionId;
    const audioEntries = [];
    if (this.preferences.saveRecordings) {
      for (const answer of records) {
        audioEntries.push({ id: `${sessionId}-${answer.id}-user`, kind: "user", turnId: answer.id, mimeType: answer.userAudio.type, blob: answer.userAudio });
        if (answer.questionAudio) audioEntries.push({ id: `${sessionId}-${answer.id}-question`, kind: "question", turnId: answer.id, mimeType: answer.questionAudio.type, blob: answer.questionAudio });
      }
    }
    const timings = {
      parts: Object.fromEntries(Object.entries(this.partTimings).map(([key, value]) => [key, Math.round(value * 10) / 10])),
      answerSeconds: Math.round(records.reduce((total, answer) => total + answer.durationSeconds, 0) * 10) / 10,
      totalSeconds: Math.round(((Date.now() - this.startedAt) / 1_000) * 10) / 10,
    };
    const session = await saveSession({
      id: sessionId,
      kind: "ielts",
      title: fullMock ? "IELTS 完整练习" : `IELTS ${section.toUpperCase()}`,
      section,
      result,
      timings,
      answers: records.map((answer) => this.publicAnswer(answer)),
      card: this.card,
    }, audioEntries);
    this.active = false;
    await saveState("activePractice", null);
    this.emit("assessment", { result, timings, session, answers: session.answers, card: this.card });
    return result;
  }

  /**
   * 移除不能持久化到 JSON 历史中的 PCM 和 Blob 字段。
   *
   * @param {object} answer 内存回答记录。
   * @returns {object} 可持久化公开回答。
   */
  publicAnswer(answer) {
    const { pcm, userAudio, questionAudio, ...publicFields } = answer;
    return publicFields;
  }

  /**
   * 保存可恢复练习状态；录音 Blob 不写入状态快照。
   *
   * @returns {Promise<void>} 快照保存完成结果。
   */
  async persistActiveState() {
    if (!this.active || this.mode === "daily") return;
    await saveState("activePractice", {
      sessionId: this.sessionId,
      mode: this.mode,
      part: this.part,
      cardSource: this.cardSource,
      card: this.card,
      questions: this.questions,
      questionIndex: this.questionIndex,
      currentQuestion: this.currentQuestion,
      currentQuestionKind: this.currentQuestionKind,
      currentQuestionSource: this.currentQuestionSource,
      answers: this.answers.map((answer) => this.publicAnswer(answer)),
      dynamicCounts: this.dynamicCounts,
      startedAt: this.startedAt,
      partStartedAt: this.partStartedAt,
      partTimings: this.partTimings,
      savedAt: new Date().toISOString(),
    });
  }

  /**
   * 恢复 iOS 中断前的题目和文字回答，录音证据不会伪造恢复。
   *
   * @returns {Promise<boolean>} 是否成功恢复练习。
   */
  async restoreActiveState() {
    const snapshot = await getState("activePractice");
    if (!snapshot?.currentQuestion) return false;
    await this.refreshConfiguration();
    Object.assign(this, {
      sessionId: snapshot.sessionId,
      mode: snapshot.mode,
      part: snapshot.part,
      cardSource: snapshot.cardSource,
      card: snapshot.card,
      questions: snapshot.questions || [],
      questionIndex: snapshot.questionIndex || 0,
      currentQuestion: snapshot.currentQuestion,
      currentQuestionKind: snapshot.currentQuestionKind || "main",
      currentQuestionSource: snapshot.currentQuestionSource || "preset",
      answers: snapshot.answers || [],
      dynamicCounts: snapshot.dynamicCounts || { part1: 0, part3: 0 },
      startedAt: snapshot.startedAt || Date.now(),
      partStartedAt: snapshot.partStartedAt || Date.now(),
      partTimings: snapshot.partTimings || {},
      currentQuestionAudio: null,
      active: true,
    });
    this.emit("restored", { snapshot });
    this.emit("question", {
      part: this.part,
      index: this.part === "part2" ? (this.currentQuestionKind === "part2_long" ? 1 : 2) : this.questionIndex + 1,
      total: this.part === "part2" ? 2 : this.questions.length,
      text: this.currentQuestion,
      kind: this.currentQuestionKind,
      source: this.currentQuestionSource,
      audio: null,
    });
    return true;
  }
}

/**
 * 判断转写是否包含实际可用于追问的内容。
 *
 * @param {string} transcript 用户转写。
 * @returns {boolean} 是否包含非空内容。
 */
function transcriptHasContent(transcript) {
  return String(transcript || "").trim().length > 0;
}
